<template>
  <div>
    <main>
      <section id="home" class="hero-section">
        <div class="hero-content">
          <h2>Create Your Perfect Ringtone</h2>
          <p>Customize easily, make every call unique</p>
          <div class="hero-features">
            <div class="hero-feature-item">
              <span class="hero-feature-icon">🎵</span>
              <span class="hero-feature-text">10,000+ Premium Ringtones</span>
            </div>
            <div class="hero-feature-item">
              <span class="hero-feature-icon">⚡</span>
              <span class="hero-feature-text">Fast Ringtone Creation</span>
            </div>
            <div class="hero-feature-item">
              <span class="hero-feature-icon">🆓</span>
              <span class="hero-feature-text">Massive Audio Library</span>
            </div>
          </div>
          <p class="hero-description">Tired of boring ringtones? Try Hi Ringtone Maker! You can quickly create custom ringtones, import your favorite songs or choose from our extensive library, and finish creating your ringtone in just seconds.</p>
          <div class="app-actions">
            <button class="download-btn">Download Now</button>
            <button class="learn-more-btn">Learn More</button>
          </div>
        </div>
      </section>

      <section id="screenshots" class="screenshots-section">
        <h2>App Screenshots</h2>
        <p class="section-subtitle">Explore Hi Ringtone Maker's intuitive interface that makes ringtone creation simple and fun</p>
        <div class="screenshot-slider">
          <div class="screenshot-item">
            <div class="screenshot-phone">
              <div class="screenshot-image screenshot-1"></div>
            </div>
            <p>Home Screen - Browse Popular Ringtones</p>
          </div>
          <div class="screenshot-item">
            <div class="screenshot-phone">
              <div class="screenshot-image screenshot-2"></div>
            </div>
            <p>Edit Interface - Precise Audio Trimming</p>
          </div>
          <div class="screenshot-item">
            <div class="screenshot-phone">
              <div class="screenshot-image screenshot-3"></div>
            </div>
            <p>Categories - Quick Search</p>
          </div>
          <div class="screenshot-item">
            <div class="screenshot-phone">
              <div class="screenshot-image screenshot-4"></div>
            </div>
            <p>Favorites - Manage Ringtones</p>
          </div>
          <div class="screenshot-item">
            <div class="screenshot-phone">
              <div class="screenshot-image screenshot-5"></div>
            </div>
            <p>Audio Editing - Professional Processing</p>
          </div>
          <div class="screenshot-item">
            <div class="screenshot-phone">
              <div class="screenshot-image screenshot-6"></div>
            </div>
            <p>Trending - Discover Featured Content</p>
          </div>
        </div>
      </section>

      <section id="features" class="features-section">
        <h2>Rich Ringtone Library</h2>
        <p class="section-subtitle">Explore our carefully curated collection of unique ringtones: funny tones, world tones, remix tones, animal sounds, and more</p>
        <div class="features-grid">
          <div class="feature-card">
            <div class="feature-icon">🎵</div>
            <h3>Diverse Real-time Updates</h3>
            <p>Access constantly evolving premium content, including popular ringtones, funny tones, latest releases, and text notification sounds</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon">🎸</div>
            <h3>Various Music Styles</h3>
            <p>Explore hip-hop, funk, jazz, blues, rock, classical, TikTok trends, reggae, and many other music genres</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon">👤</div>
            <h3>Personalized Recommendations</h3>
            <p>Let the app curate personalized ringtone playlists that perfectly match your music preferences, making every notification enjoyable</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon">📂</div>
            <h3>Intuitive Category Browsing</h3>
            <p>Whether you prefer country melodies, movie soundtracks, ASMR whispers, or TikTok hits, our well-organized categories make it easy to find your ideal sound</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon">🌟</div>
            <h3>Weekly Featured Selection</h3>
            <p>Weekly updated featured trending ringtones, covering popular songs, seasonal themes, and current trends, keeping your ringtones at the cutting edge</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon">🔊</div>
            <h3>Sound Effects Section</h3>
            <p>Unique sound effects library including nature sounds, animal effects, sci-fi tones, and humorous alerts, providing perfect choices for different occasions</p>
          </div>
        </div>
      </section>

      <section id="create" class="creation-section">
        <h2>Creation Features</h2>
        <p class="section-subtitle">Powerful audio editing tools that make ringtone creation effortless</p>
        
        <div class="creation-grid">
          <div class="creation-item">
            <div class="creation-icon">🎬</div>
            <h3>Video to MP3</h3>
            <p>Easily extract audio from videos in your album or computer, instantly transform great video moments into unique ringtones</p>
          </div>
          <div class="creation-item">
            <div class="creation-icon">✂️</div>
            <h3>Audio Editor</h3>
            <p>Precise audio trimming down to 0.1 seconds, add 1-5 second fade in/out effects, create professional-quality sound effects</p>
          </div>
          <div class="creation-item">
            <div class="creation-icon">🔄</div>
            <h3>Format Conversion</h3>
            <p>Convert audio and video files to mp3 or m4r formats, ensuring compatibility with your devices</p>
          </div>
          <div class="creation-item">
            <div class="creation-icon">🧩</div>
            <h3>Audio Mixing</h3>
            <p>Combine multiple audio files to create complex and personalized ringtone compositions</p>
          </div>
          <div class="creation-item">
            <div class="creation-icon">🎙️</div>
            <h3>Recording Function</h3>
            <p>Record voices of family and friends, or any sounds you like, to create exclusive ringtones</p>
          </div>
          <div class="creation-item">
            <div class="creation-icon">🎚️</div>
            <h3>Parameter Adjustment</h3>
            <p>Fine-tune pitch, volume, and easily manipulate start and end markers to perfect your creation</p>
          </div>
        </div>
      </section>
      
      <section id="resource" class="resource-section">
        <h2>Resource Integration</h2>
        <p class="section-subtitle">Import audio from multiple channels to expand your creative materials</p>
        
        <div class="resource-container">
          <div class="resource-item">
            <div class="resource-icon">📱</div>
            <h3>Local MP3 Usage</h3>
            <p>Create ringtones from iPhone local files, music library, iTunes, and Files app to access your personal music collection</p>
          </div>
          <div class="resource-item">
            <div class="resource-icon">🌐</div>
            <h3>External Import</h3>
            <p>Import your favorite popular music and songs from various sources to expand your creative material library</p>
          </div>
          <div class="resource-item">
            <div class="resource-icon">💾</div>
            <h3>Personal Collection</h3>
            <p>Save and edit your favorite ringtones, alert tones, and alarm sounds in a dedicated "My Favorites" folder for easy access and future modifications</p>
          </div>
          <div class="resource-item">
            <div class="resource-icon">📤</div>
            <h3>Installation & Sharing</h3>
            <p>Easily install custom ringtones via GarageBand and iTunes, simplify the setup process, and share your unique ringtone creations with friends</p>
          </div>
          <div class="resource-item">
            <div class="resource-icon">📱</div>
            <h3>Cross-Platform Compatibility</h3>
            <p>Support for iOS and Android platforms, ensuring your created ringtones work perfectly on different devices, providing a consistent audio experience</p>
          </div>
          <div class="resource-item">
            <div class="resource-icon">🎨</div>
            <h3>Creative Resource Library</h3>
            <p>Access professionally designed sound effects, music clips, and soundtrack materials to provide unlimited inspiration for your ringtone creations</p>
          </div>
        </div>
      </section>

      <section class="tutorial-section">
        <div class="tutorial-content">
          <h2>Comprehensive Guidance</h2>
          <p class="section-subtitle">Super detailed text and video tutorials guide you through every step, ensuring 100% success in creating iPhone ringtones</p>
          
          <div class="tutorial-features">
            <div class="tutorial-feature">
              <div class="tutorial-icon">📚</div>
              <h3>Detailed Text Tutorials</h3>
              <p>Clear step-by-step guidance makes it easy even for beginners to get started</p>
            </div>
            <div class="tutorial-feature">
              <div class="tutorial-icon">🎥</div>
              <h3>Video Demonstrations</h3>
              <p>Watch operation demos to visually understand how to use each feature</p>
            </div>
            <div class="tutorial-feature">
              <div class="tutorial-icon">❓</div>
              <h3>FAQ Section</h3>
              <p>Solutions to potential issues you may encounter during use, ensuring smooth creation</p>
            </div>
          </div>
        </div>
      </section>

      <section id="community" class="community-section">
        <div class="community-content">
          <h2>User Feedback</h2>
          <p class="section-subtitle">The choice of millions of users, providing a professional ringtone creation experience</p>
          
          <div class="community-features">
            <div class="community-feature">
              <div class="community-icon">⭐</div>
              <h3>Rave Beta Reviews</h3>
              <p>Consistently positive reviews from beta testers, with experiences exceeding expectations</p>
            </div>
            <div class="community-feature">
              <div class="community-icon">👍</div>
              <h3>Early User Recommendations</h3>
              <p>Beta users actively recommend to friends, becoming the main force in word-of-mouth promotion</p>
            </div>
            <div class="community-feature">
              <div class="community-icon">💬</div>
              <h3>Rapid Iterative Updates</h3>
              <p>Team actively responds to user feedback, continuously optimizing product experience</p>
            </div>
          </div>
          
          <div class="social-share">
            <p>Please rate us if satisfied</p>
            <div class="social-icons">
              <a href="#" class="social-icon">App Store</a>
              <a href="#" class="social-icon">Google Play</a>
            </div>
          </div>
        </div>
      </section>

      <section id="download" class="download-section">
        <div class="download-content">
          <h2>Download Now</h2>
          <p class="section-subtitle">Hi Ringtone Maker is now available, set the perfect ringtone for your phone anytime, anywhere</p>
          <div class="app-subtitle">iPhone Custom Ringtones</div>
          <div class="keywords-tag">
            <span class="keyword">ringtone maker</span>
            <span class="keyword">garageband</span>
            <span class="keyword">custom ringtones</span>
            <span class="keyword">ring app</span>
          </div>
          <div class="download-buttons">
            <a href="#" class="store-button google-play">
              <span class="store-icon">🤖</span>
              <div class="store-text">
                <span class="get-text">GET IT ON</span>
                <span class="store-name">Google Play</span>
              </div>
            </a>
            <a href="#" class="store-button app-store">
              <span class="store-icon">🍎</span>
              <div class="store-text">
                <span class="get-text">Download on the</span>
                <span class="store-name">App Store</span>
              </div>
            </a>
          </div>
        </div>
      </section>
    </main>

    <footer class="footer">
      <div class="footer-wave">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320"><path fill="#ff7f66" fill-opacity="0.05" d="M0,128L48,138.7C96,149,192,171,288,165.3C384,160,480,128,576,128C672,128,768,160,864,181.3C960,203,1056,213,1152,208C1248,203,1344,181,1392,170.7L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path></svg>
      </div>
      <div class="footer-content">
        <div class="footer-main">
          <div class="footer-logo">
            <img src="@/assets/logo.png" alt="Logo" class="footer-favicon">
            <h3>Hi Ringtone Maker</h3>
          </div>
          <p class="footer-description">Create your perfect ringtone, make every call unique</p>
          <p class="footer-copyright">© 2025 All Rights Reserved</p>
        </div>
        
        <div class="footer-links">
          <div class="footer-links-col">
            <h4>Navigation</h4>
            <ul>
              <li><a href="#home">Home</a></li>
              <li><a href="#features">Ringtone Library</a></li>
              <li><a href="#create">Creation Features</a></li>
            </ul>
          </div>
          
          <div class="footer-links-col">
            <h4>More</h4>
            <ul>
              <li><a href="#resource">Resources</a></li>
              <li><a href="#community">User Feedback</a></li>
              <li><a href="#download">Download</a></li>
            </ul>
          </div>
          
          <div class="footer-links-col">
            <h4>Contact</h4>
            <p class="contact-item">
              <span class="contact-icon">✉️</span>
              <a href="mailto:yikenxinxin@gmail.com" class="contact-email">yikenxinxin@gmail.com</a>
            </p>
          </div>
        </div>
      </div>
      
      <div class="copyright-bar">
        <div class="copyright-content">
          <p>© 2025 Hi Ringtone Maker</p>
          <div class="policy-links">
            <router-link to="/privacy-policy" class="policy-link-btn" style="pointer-events: auto; z-index: 999; position: relative;">Privacy Policy</router-link>
            <span class="divider">|</span>
            <router-link to="/Service_Terms" class="policy-link-btn" style="pointer-events: auto; z-index: 999; position: relative;">Terms of Service</router-link>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'HomeView'
}
</script>

<style scoped>
/* Styles can be imported here */
</style> 